// eslint-disable-next-line no-unused-vars
import React, {useState, useRef, useEffect, useCallback} from 'react';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import {getAllLuckyWheel, spinLuckyWheel, getLuckyWheelNextSpinTime, getActiveServers} from '../../../api/axios';
import { useAuth } from '../../../hooks/useAuth';
import { Navigate} from 'react-router-dom';
import ServerSwitch from '../serverSwitch';

export const defaultSegmentColors = [
  {color: '#FFAA00'},
  {color: '#90EE90'},
  {color: '#32CD32'},
  {color: '#1E90FF'},
  {color: '#9370DB'},
  {color: '#FFA07A'},
  {color: '#FF4500'},
  {color: '#DC143C'},
  {color: '#FF1493'},
  {color: '#FFD700'},
];
const WheelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0 20px;
  position: relative;
`;

const CanvasContainer = styled.div`
  position: relative;
  width: 500px; /* Größere Canvas-Größe für mehr Details */
  height: 500px;
  @media (max-width: 768px) {
    width: 300px; /* Größere Canvas-Größe für mehr Details */
    height: 300px;
  }
`;

/* Goldener Zeiger mit Schatten-Effekt */
const Pointer = styled.div`
  position: absolute;
  top: 10px;
  left: 50%;
  transform: rotate(180deg) translateX(45%);
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 50px solid #ffd700; /* Gold */
  filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.5));
  z-index: 2;
`;

const StyledButton = styled.button`
  padding: 10px;
  font-size: 1rem;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin: 20px auto;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StyledWin = styled.div`
  height: 50px;
  text-align: center;
  font-size: 1.2rem;
  color: green;
  margin-bottom: 80px;

  @media (max-width: 768px) {
    margin-bottom: 140px;
  }
`;

// Tooltip-DIV (absolut positioniert)
const Tooltip = styled.div`
  position: absolute;
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  padding: 5px 8px;
  border-radius: 4px;
  font-size: 0.9rem;
  pointer-events: none;
  z-index: 10;
`;

const Spinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const SpinnerOverlay = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const StypedDiv = styled.div`
  color: #fff;
`;

const StypedEventSpan = styled.span`
  color: #fff555;
`;

const LuckyWheel = () => {
  const {t} = useTranslation();
  const canvasRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState(500);
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [winningPrize, setWinningPrize] = useState(null);
  const [blinkOn, setBlinkOn] = useState(true);
  const [nextSpin, setNextSpin] = useState(null);
  const [spinInterval, setSpinInterval] = useState(12);

  // Neue States für Tooltip
  const [hoveredPrize, setHoveredPrize] = useState(null);
  const [hoverPos, setHoverPos] = useState({x: 0, y: 0});

  // State für die Items aus der DB (statt Dummy-Prizes)
  const [prizes, setPrizes] = useState([]);
  // State, um die geladenen Bilder zu speichern (Key: product_id)
  const [prizeImages, setPrizeImages] = useState({});
  const {user } = useAuth();

  const [sv, setSv] = useState('hybrid');
  const [activeServers, setActiveServers] = useState([]);
  const showServerSwitch = activeServers.length > 1;


  const [isMuted] = useState(() => {
    const stored = localStorage.getItem('luckyWheelMuted');
    return stored ? JSON.parse(stored) : false;
  });

  // Audio-Objekt für Sound
  const spinSoundRef = useRef(null);
  useEffect(() => {
    spinSoundRef.current = new Audio('/assets/sound/lucky_wheel.mp3');
    // Setze den Audio-Muted-Zustand beim Mount
    if (spinSoundRef.current) {
      spinSoundRef.current.muted = isMuted;
    }
 // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (spinSoundRef.current) {
      spinSoundRef.current.muted = isMuted;
    }
  }, [isMuted]);


  // 🔹 Active Server laden (wie im ShopPage)
  useEffect(() => {
    let mounted = true;
    getActiveServers()
      .then(res => {
        if (!mounted) return;
        const list = Array.isArray(res?.data?.servers) ? res.data.servers : [];
        setActiveServers(list);
        // genau 1 aktiv -> den setzen
        if (list.length === 1 && list[0]?.friendly_key) setSv(list[0].friendly_key);
        // mehrere aktiv, aktueller sv nicht enthalten -> ersten aktiven setzen
        if (list.length > 1 && !list.find(s => s.friendly_key === sv) && list[0]?.friendly_key) {
          setSv(list[0].friendly_key);
        }
      })
      .catch(() => setActiveServers([]));
    return () => {
      mounted = false;
    };
  }, [sv]);

  // Aktualisiere die Canvas-Größe basierend auf der Fensterbreite
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setCanvasSize(300);
      } else {
        setCanvasSize(500);
      }
    };

    window.addEventListener('resize', handleResize);
    // Initialer Aufruf:
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Lade die Prizes via API
  useEffect(() => {
    const fetchPrizes = async () => {
      try {
        const response = await getAllLuckyWheel();
        setSpinInterval(response.data.spin_interval);
        setPrizes(response.data.items);
      } catch (err) {
        //
      }
    };
    fetchPrizes();
  }, []);

  useEffect(() => {
    const fetchPrizes = async () => {
      try {
        const response = await getLuckyWheelNextSpinTime();
        setNextSpin(response.data);
      } catch (err) {
        //
      }
    };
    fetchPrizes();
  }, []);

  // Lade zu jedem Prize das Icon aus /assets/items/
  useEffect(() => {
    const loadImages = async () => {
      const loadedImages = {};
      await Promise.all(
        prizes.map(
          prize =>
            new Promise(resolve => {
              const img = new Image();
              img.src = `/assets/items/${prize.item_icon}.jpg`;
              img.onload = () => {
                loadedImages[prize.product_id] = img;
                resolve();
              };
              img.onerror = () => {
                const fallback = new Image();
                fallback.src = `/assets/items/unknown.jpg`;
                fallback.onload = () => {
                  loadedImages[prize.product_id] = fallback;
                  resolve();
                };
                fallback.onerror = () => {
                  loadedImages[prize.product_id] = null;
                  resolve();
                };
              };
            })
        )
      );
      setPrizeImages(loadedImages);
    };
    if (prizes.length > 0) {
      loadImages();
    }
  }, [prizes]);

  // Mouse-Move-Handler: Ermittelt, ob die Maus über einem Icon schwebt
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      // Skalierungsfaktoren, falls der Canvas per CSS skaliert wird
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      // Mausposition in Canvas-Pixelkoordinaten
      const mouseX = (e.clientX - rect.left) * scaleX;
      const mouseY = (e.clientY - rect.top) * scaleY;
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const numSegments = prizes.length;
      const innerRadius = canvas.width / 2 - 30;
      const iconRadius = innerRadius * 0.7; // wie in drawWheel
      let found = false;
      for (let i = 0; i < numSegments; i++) {
        // Berechne den Winkel für das Icon des Segments:
        const angle = ((rotation + (i + 0.5) * (360 / numSegments)) * Math.PI) / 180;
        // Icon-Mittelpunkt in Canvas-Pixelkoordinaten:
        const iconX = centerX + iconRadius * Math.cos(angle);
        const iconY = centerY + iconRadius * Math.sin(angle);
        const dx = mouseX - iconX;
        const dy = mouseY - iconY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        // Erhöhe den Schwellenwert (z. B. 30px) für mehr Toleranz
        if (distance < 30) {
          // Tooltip an Mausposition (oder Icon-Position) setzen
          setHoveredPrize(prizes[i]);
          setHoverPos({ x: e.mouseX, y: e.mouseY });
          found = true;
          break;
        }
      }
      if (!found) {
        setHoveredPrize(null);
      }
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
    };
  }, [rotation, prizes, canvasSize]);


  const drawWheel = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || prizes.length === 0) return;
    const ctx = canvas.getContext('2d');
    const {width, height} = canvas;
    const numSegments = prizes.length;
    const anglePerSegment = (2 * Math.PI) / numSegments;
    const radius = width / 2;

    ctx.clearRect(0, 0, width, height);

    ctx.save();
    ctx.translate(radius, radius);

    // Außenring mit Gradient
    const outerRingGradient = ctx.createRadialGradient(0, 0, radius * 0.4, 0, 0, radius);
    outerRingGradient.addColorStop(0, '#FFD700');
    outerRingGradient.addColorStop(1, '#B8860B');
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, 2 * Math.PI);
    ctx.fillStyle = outerRingGradient;
    ctx.fill();

    // Lichterkette
    const numLights = 30;
    const lightRadius = 6;
    const lightOffset = radius - 10;
    const colorA = blinkOn ? '#ff0000' : '#000000';
    const colorB = blinkOn ? '#000000' : '#ff0000';
    for (let i = 0; i < numLights; i++) {
      const angle = (2 * Math.PI * i) / numLights;
      const x = Math.cos(angle) * lightOffset;
      const y = Math.sin(angle) * lightOffset;
      ctx.beginPath();
      ctx.fillStyle = i % 2 === 0 ? colorA : colorB;
      ctx.arc(x, y, lightRadius, 0, 2 * Math.PI);
      ctx.fill();
    }

    // Innenkreis für Segmente
    const innerRadius = radius - 30;
    ctx.rotate((rotation * Math.PI) / 180);
    prizes.forEach((prize, index) => {
      const startAngle = index * anglePerSegment;
      const endAngle = (index + 1) * anglePerSegment;

      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, innerRadius, startAngle, endAngle);
      // Falls aus DB kein Farbfeld kommt, kannst du hier einen Standardwert setzen:
      ctx.fillStyle = defaultSegmentColors[index % defaultSegmentColors.length].color;
      ctx.fill();

      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.save();
      const drawAngle = startAngle + anglePerSegment / 2;
      ctx.rotate(drawAngle);
      // Anstatt Text zeichnen, jetzt das Icon-Bild einfügen:
      const img = prizeImages[prize.product_id];
      const imgWidth = canvas.width <= 300 ? 30 : 40;
      const imgHeight = canvas.width <= 300 ? 30 : 40;
      if (img) {
        ctx.drawImage(img, innerRadius * 0.7 - imgWidth / 2, -imgHeight / 2, imgWidth, imgHeight);
      } else {
        // Fallback: Den item_name als Text anzeigen
        ctx.font = 'bold 20px Arial';
        ctx.fillStyle = '#fff';
        ctx.fillText(prize.item_name, innerRadius * 0.6, 0);
      }
      ctx.restore();
    });

    // Stern in der Mitte
    const drawStar = (cx, cy, spikes, outerR, innerR) => {
      let rot = (Math.PI / 2) * 3;
      let x = cx;
      let y = cy;
      const step = Math.PI / spikes;
      ctx.beginPath();
      ctx.moveTo(cx, cy - outerR);
      for (let i = 0; i < spikes; i++) {
        x = cx + Math.cos(rot) * outerR;
        y = cy + Math.sin(rot) * outerR;
        ctx.lineTo(x, y);
        rot += step;
        x = cx + Math.cos(rot) * innerR;
        y = cy + Math.sin(rot) * innerR;
        ctx.lineTo(x, y);
        rot += step;
      }
      ctx.lineTo(cx, cy - outerR);
      ctx.closePath();
    };
    ctx.save();
    ctx.rotate(0);
    drawStar(0, 0, 5, 20, 10);
    ctx.fillStyle = '#FFD700';
    ctx.fill();
    ctx.restore();

    ctx.restore();
  }, [rotation, prizes, blinkOn, prizeImages]);

  useEffect(() => {
    drawWheel();
  }, [rotation, drawWheel]);

  useEffect(() => {
    if (canvasRef.current) {
      // Setze die Canvas-Dimensionen neu
      canvasRef.current.width = canvasSize;
      canvasRef.current.height = canvasSize;
      // Zeichne den Canvas erneut (damit der Inhalt wieder erscheint)
      drawWheel();
    }
  }, [canvasSize, drawWheel]);

  /**
   * Spin-Logik mit easing, die sicherstellt, dass das Rad mindestens
   * eine Mindestdrehung macht und immer in eine Richtung dreht.
   */
  const spinWheel = async () => {
    if (isSpinning) return;
    setIsSpinning(true);
    setWinningPrize(null);

    let targetIndex, returnedNextSpin;

    try {
      // Der axios-Aufruf liefert ein JSON-Objekt mit winningIndex, winningPrize und nextSpin
      const response = await spinLuckyWheel(sv);
      const data = response.data;
      targetIndex = data.winningIndex;
      returnedNextSpin = data.nextSpin; // Speichere den Wert, aber setze ihn noch nicht in den State
    } catch (error) {
      //
    }

    // Sound starten (falls vorhanden)
    if (spinSoundRef.current) {
      spinSoundRef.current.currentTime = 0;
      spinSoundRef.current.play().catch(err => {
        //
      });
    }

    // Blink-Intervall (alle 300ms toggeln)
    const blinkInterval = setInterval(() => {
      setBlinkOn(prev => !prev);
    }, 150);

    const duration = 5000; // Animationsdauer in ms
    let startTime = null;

    // Normalisiere den aktuellen Winkel in den Bereich 0–360
    const normalizedRotation = ((rotation % 360) + 360) % 360;
    // Aktueller Winkel (absolut, kann >360 sein)
    const currentRotation = rotation;

    // Berechnung des Zielwinkels:
    const numSegments = prizes.length;
    const segmentAngle = 360 / numSegments;
    // Segmentmitte des gewünschten Segments (z.B. für Index 0: 18°, Index 1: 54°, …)
    const segmentCenter = (targetIndex + 0.5) * segmentAngle;
    // Ziel: Das gewünschte Segment soll oben (Canvas "oben" entspricht 270°) liegen.
    // Das heißt, targetAngle (im 0–360‑Bereich) soll sein:
    const targetAngle = (270 - segmentCenter + 360) % 360;

    // Berechne die Differenz vom aktuellen (normalisierten) Winkel zum Zielwinkel:
    let delta = (targetAngle - normalizedRotation + 360) % 360;
    // Mindestens 720° zusätzlich erzwingen (zwei volle Umdrehungen)
    const minDelta = 1440;
    while (delta < minDelta) {
      delta += 360;
    }
    // Das Endergebnis (absolut) ist der aktuelle Winkel plus delta
    const finalAngle = currentRotation + delta;

    const totalDelta = finalAngle - currentRotation;

    // Verwende eine exponentielle Ease-Out-Funktion für einen schnellen Start
    const easeOutExpo = t => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

    const animate = timestamp => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      let progress = elapsed / duration;
      if (progress > 1) progress = 1;
      const eased = easeOutExpo(progress);
      const newAngle = currentRotation + totalDelta * eased;
      setRotation(newAngle);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Animation beendet: exakt finalAngle setzen
        setRotation(finalAngle);
        setIsSpinning(false);
        clearInterval(blinkInterval);
        setBlinkOn(true);

        if (spinSoundRef.current) {
          spinSoundRef.current.pause();
          spinSoundRef.current.currentTime = 0;
        }

        setWinningPrize(prizes[targetIndex]);
        setNextSpin(returnedNextSpin);
      }
    };

    requestAnimationFrame(animate);
  };

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return (
    <WheelContainer>
          {showServerSwitch && (
        <div style={{display: 'flex', justifyContent: 'flex-end', width: '100%', marginBottom: '8px'}}>
          <ServerSwitch
            value={sv}
            onChange={setSv}
            label={t('wheel.server')}
            options={activeServers.map(s => ({
              value: s.friendly_key,
              label: s.display_name || s.friendly_key,
            }))}
          />
        </div>
      )}
      <StyledWin>
        <StypedDiv>
          <strong>
            {spinInterval !== 12 ? <StypedEventSpan>{t('wheel.event')}</StypedEventSpan> : ''}{' '}
            {t('wheel.description', {spinInterval})}
          </strong>
        </StypedDiv>
        {winningPrize && (
          <div>
            <strong>
              {t('wheel.winMessage', {
                count: winningPrize.item_count,
                itemName: winningPrize.item_name,
              })}
            </strong>
          </div>
        )}
        <div>
          <strong>{t('wheel.nextSpin', {nextSpin: nextSpin || t('wheel.now')})}</strong>
        </div>
      </StyledWin>

      <CanvasContainer>
        {prizes.length === 0 || Object.keys(prizeImages).length === 0 ? (
          <SpinnerOverlay>
            <Spinner />
          </SpinnerOverlay>
        ) : (
          <>
            <Pointer />
            <canvas ref={canvasRef} width={canvasSize} height={canvasSize} />
          </>
        )}
      </CanvasContainer>

      <StyledButton onClick={spinWheel} disabled={isSpinning || (nextSpin && new Date() < new Date(nextSpin))}>
        {t('wheel.spinButton')}
      </StyledButton>

      {hoveredPrize && (
        <Tooltip style={{top: hoverPos.y + 10, left: hoverPos.x + 10}}>
          {t('wheel.tooltip', {
            count: hoveredPrize.item_count,
            itemName: hoveredPrize.item_name,
          })}
        </Tooltip>
      )}
    </WheelContainer>
  );
};

export default LuckyWheel;
